import { useEffect, useRef } from 'react';

interface GlowCardProps {
  children: React.ReactNode;
  className?: string;
}

export function GlowCard({ children, className = '' }: GlowCardProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = ref.current;
    if (!card) return;

    let rafId: number | null = null;
    let cursorX = 0;
    let cursorY = 0;

    const glow = document.createElement('div');
    glow.className = 'pix3l-glow';
    glow.setAttribute('aria-hidden', 'true');
    card.insertBefore(glow, card.firstChild);

    function applyGlow() {
      const rect = card!.getBoundingClientRect();
      const px = ((cursorX - rect.left) / rect.width) * 100;
      const py = ((cursorY - rect.top) / rect.height) * 100;
      const dx = cursorX - (rect.left + rect.width / 2);
      const dy = cursorY - (rect.top + rect.height / 2);
      const angleDeg = Math.atan2(dy, dx) * (180 / Math.PI);
      glow.style.setProperty('--glow-x', `${px.toFixed(2)}%`);
      glow.style.setProperty('--glow-y', `${py.toFixed(2)}%`);
      glow.style.setProperty('--glow-start', `${angleDeg.toFixed(2)}deg`);
      rafId = null;
    }

    function onEnter(e: MouseEvent) {
      cursorX = e.clientX;
      cursorY = e.clientY;
      glow.classList.add('glow-visible');
      if (!rafId) rafId = requestAnimationFrame(applyGlow);
    }

    function onMove(e: MouseEvent) {
      cursorX = e.clientX;
      cursorY = e.clientY;
      if (!rafId) rafId = requestAnimationFrame(applyGlow);
    }

    function onLeave() {
      glow.classList.remove('glow-visible');
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    card.addEventListener('mouseenter', onEnter);
    card.addEventListener('mousemove', onMove);
    card.addEventListener('mouseleave', onLeave);

    return () => {
      card.removeEventListener('mouseenter', onEnter);
      card.removeEventListener('mousemove', onMove);
      card.removeEventListener('mouseleave', onLeave);
      glow.remove();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {children}
    </div>
  );
}
